import React, { useEffect, useState } from 'react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, Save, Lock, FormInput } from 'lucide-react';
import { FormLivePreview } from './FormLivePreview';
import { formatApiError } from '@/lib/errors';

const LOCKED = new Set([
  'student.first_name', 'student.last_name', 'student.instrument',
  'payer.first_name', 'payer.last_name', 'payer.email',
]);

const FIELD_GROUPS = [
  {
    title: 'Schritt: Fach auswählen',
    keys: ['student.category', 'student.instrument', 'student.notes'],
  },
  {
    title: 'Schritt: Schüler:in / Teilnehmer:in',
    keys: ['student.salutation', 'student.title', 'student.first_name', 'student.last_name', 'student.gender', 'student.birthdate', 'student.email', 'student.phone', 'student.prior_knowledge', 'student.photo_consent'],
  },
  {
    title: 'Schritt: Zahler & Adresse',
    keys: ['payer.salutation', 'payer.title', 'payer.first_name', 'payer.last_name', 'payer.street', 'payer.house_number', 'payer.address_extra', 'payer.postal_code', 'payer.city', 'payer.district', 'payer.gender', 'payer.birthdate', 'payer.email', 'payer.phone'],
  },
];

const TEXT_FIELDS = [
  { key: 'step_subject_title', label: 'Schritt 1 — Fach und Unterrichtsform' },
  { key: 'step_student_title', label: 'Schritt — Teilnehmer:in' },
  { key: 'step_slot_title', label: 'Schritt — Verfügbarkeit' },
  { key: 'step_payer_title', label: 'Schritt — Zahlung und Adresse' },
  { key: 'step_payer_hint', label: 'Hinweistext „Zahlung und Adresse"' },
  { key: 'step_sepa_title', label: 'Schritt — Lastschriftmandat' },
  { key: 'step_guardian_title', label: 'Schritt — Erziehungsberechtigte:r' },
  { key: 'step_sepa_hint', label: 'Hinweistext „Lastschrift"' },
  { key: 'step_summary_title', label: 'Schritt — Prüfen und absenden' },
  { key: 'submit_button', label: 'Text Absende-Button' },
];

const CF_TYPES = [
  { value: 'text', label: 'Textfeld' },
  { value: 'textarea', label: 'Mehrzeiliger Text' },
  { value: 'select', label: 'Auswahlliste' },
  { value: 'checkbox', label: 'Checkbox' },
];

export const RegistrationFormEditor = ({ tenantId }) => {
  const [fc, setFc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    api.get(`/tenants/${tenantId}/registration-settings`)
      .then((r) => setFc(r.data.form_config || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [tenantId]);

  const save = async () => {
    if (!tenantId) {
      toast.error('Kein Mandant ausgewählt — bitte oben links den Mandanten wählen.');
      return;
    }
    setSaving(true);
    try {
      const r = await api.put(`/tenants/${tenantId}/registration-settings`, { form_config: fc });
      setFc(r.data.form_config);
      toast.success('Formular gespeichert');
    } catch (e) {
      toast.error(formatApiError(e, 'Speichern fehlgeschlagen'));
    } finally {
      setSaving(false);
    }
  };

  const setStep = (key, v) => setFc((p) => ({ ...p, steps: { ...p.steps, [key]: v } }));
  const setAskTeacherFirst = (v) => setFc((p) => ({
    ...p,
    coordination: { ...(p.coordination || {}), ask_teacher_first: v },
  }));
  const setVer = (key, v) => setFc((p) => ({ ...p, verification: { ...(p.verification || {}), [key]: v } }));
  const setText = (key, v) => setFc((p) => ({ ...p, texts: { ...p.texts, [key]: v } }));
  const setFieldSpec = (key, patch) => setFc((p) => ({
    ...p,
    fields: { ...p.fields, [key]: { ...p.fields[key], ...patch } },
  }));

  const addCustomField = () => setFc((p) => ({
    ...p,
    custom_fields: [
      ...(p.custom_fields || []),
      { id: `cf_${Math.random().toString(36).slice(2, 12)}`, label: '', type: 'text', step: 'student', required: false, options: [] },
    ],
  }));
  const updateCustomField = (idx, patch) => setFc((p) => ({
    ...p,
    custom_fields: p.custom_fields.map((cf, i) => (i === idx ? { ...cf, ...patch } : cf)),
  }));
  const removeCustomField = (idx) => setFc((p) => ({
    ...p,
    custom_fields: p.custom_fields.filter((_, i) => i !== idx),
  }));

  if (loading) return <div className="text-sm text-slate-500 py-4">Formular-Editor wird geladen …</div>;
  if (!fc) return null;

  return (
    <div data-testid="registration-form-editor">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-8 h-8 rounded-lg bg-brand-red/5 flex items-center justify-center">
          <FormInput className="w-4 h-4 text-brand-red" />
        </div>
        <div>
          <h3 className="font-semibold text-slate-900">Formular bearbeiten</h3>
          <p className="text-sm text-slate-500">
            Schritte, Felder, Beschriftungen und eigene Zusatzfelder des öffentlichen Anmeldeformulars.
          </p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,440px)] gap-6 items-start">
      <div>
      <div className="mb-6" data-testid="form-editor-coordination">
        <h4 className="text-sm font-semibold text-slate-700 mb-2">Ablauf nach der Anmeldung</h4>
        <div className="flex items-center justify-between border border-slate-200 rounded-lg p-3 gap-4">
          <div>
            <div className="text-sm font-medium text-slate-900">Zuerst die Lehrkraft anfragen</div>
            <div className="text-xs text-slate-500">
              Nach der E-Mail-Bestätigung fragt das System die Lehrkraft zum Wunschtermin.
              Die Verwaltung wird informiert, sobald die Lehrkraft geantwortet hat.
              Ohne Wunschtermin geht die Anmeldung direkt an die Verwaltung.
              Ausgeschaltet bleibt es bei der Inbox: die Verwaltung fragt die Lehrkraft selbst an.
            </div>
          </div>
          <Switch
            checked={!!fc.coordination?.ask_teacher_first}
            onCheckedChange={setAskTeacherFirst}
            data-testid="form-editor-ask-teacher-first"
          />
        </div>
      </div>

      {/* Schritte */}
      <div>
        <h4 className="text-sm font-semibold text-slate-700 mb-2">Schritte</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex items-center justify-between border border-slate-200 rounded-lg p-3">
            <div>
              <div className="text-sm font-medium text-slate-900">Verfügbarkeit</div>
              <div className="text-xs text-slate-500">Eltern geben Tage und Uhrzeiten an, an denen Unterricht möglich ist.</div>
            </div>
            <Switch checked={!!fc.steps.slot} onCheckedChange={(v) => setStep('slot', v)} data-testid="form-editor-step-slot-toggle" />
          </div>
          <div className="flex items-center justify-between border border-slate-200 rounded-lg p-3">
            <div>
              <div className="text-sm font-medium text-slate-900">SEPA-Schritt</div>
              <div className="text-xs text-slate-500">SEPA-Lastschriftmandat direkt bei der Anmeldung.</div>
            </div>
            <Switch checked={!!fc.steps.sepa} onCheckedChange={(v) => setStep('sepa', v)} data-testid="form-editor-step-sepa-toggle" />
          </div>
          <div className="flex items-center justify-between border border-slate-200 rounded-lg p-3">
            <div>
              <div className="text-sm font-medium text-slate-900">Erziehungsberechtigte:r-Schritt</div>
              <div className="text-xs text-slate-500">Abfrage, ob Zahler auch erziehungsberechtigt ist (sonst abweichende Person).</div>
            </div>
            <Switch checked={!!fc.steps.guardian} onCheckedChange={(v) => setStep('guardian', v)} data-testid="form-editor-step-guardian-toggle" />
          </div>
        </div>
      </div>

      {/* Verifikationen */}
      <div className="mt-6">
        <h4 className="text-sm font-semibold text-slate-700 mb-2">Verifikationen (Zahler)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex items-center justify-between border border-slate-200 rounded-lg p-3">
            <div>
              <div className="text-sm font-medium text-slate-900">SMS-Verifikation Pflicht</div>
              <div className="text-xs text-slate-500">Rufnummer muss per SMS-Code bestätigt werden, bevor die Anmeldung abgeschickt werden kann.</div>
            </div>
            <Switch checked={!!fc.verification?.phone_required} onCheckedChange={(v) => setVer('phone_required', v)} data-testid="form-editor-verify-phone-toggle" />
          </div>
        </div>
      </div>

      {/* Texte */}
      <div className="mt-6">
        <h4 className="text-sm font-semibold text-slate-700 mb-2">Überschriften & Texte</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {TEXT_FIELDS.map((t) => (
            <div key={t.key}>
              <Label className="text-xs text-slate-500">{t.label}</Label>
              <Input
                value={fc.texts[t.key] || ''}
                onChange={(e) => setText(t.key, e.target.value)}
                data-testid={`form-editor-text-${t.key}`}
              />
            </div>
          ))}
        </div>
        <div className="mt-4 p-3 rounded-lg border border-amber-200 bg-amber-50/60" data-testid="form-editor-button-legal">
          <Label className="text-xs text-slate-600">Hinweis unter dem Absende-Button</Label>
          <Textarea
            rows={3}
            className="mt-1 bg-white"
            value={fc.texts.submit_legal_hint || ''}
            onChange={(e) => setText('submit_legal_hint', e.target.value)}
            data-testid="form-editor-text-submit_legal_hint"
          />
          <p className="text-xs text-slate-600 mt-2">
            Das Formular ist eine Anmeldung mit anschließender E-Mail-Bestätigung und Prüfung durch die Schule — kein Sofortkauf.
            Deshalb lautet der Standard „Anmeldung absenden“. Wenn Sie später einen direkt verbindlichen, entgeltlichen
            Fernabsatzvertrag schließen, verlangt § 312j BGB eine eindeutige Formulierung wie
            {' '}
            <strong>„Kostenpflichtig anmelden“</strong>
            {' '}
            (nicht die Shop-Formel „Zahlungspflichtig bestellen“).
          </p>
        </div>
      </div>

      {/* Felder */}
      <div className="mt-6">
        <h4 className="text-sm font-semibold text-slate-700 mb-2">Felder</h4>
        <div className="space-y-4">
          {FIELD_GROUPS.map((grp) => (
            <div key={grp.title} className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {grp.title}
              </div>
              <div className="divide-y divide-slate-100">
                {grp.keys.map((key) => {
                  const spec = fc.fields[key] || {};
                  const locked = LOCKED.has(key);
                  return (
                    <div key={key} className="flex flex-wrap items-center gap-3 px-4 py-2" data-testid={`form-editor-field-${key.replace('.', '-')}`}>
                      <div className="flex-1 min-w-[200px]">
                        <Input
                          value={spec.label || ''}
                          onChange={(e) => setFieldSpec(key, { label: e.target.value })}
                          className="h-8 text-sm"
                          data-testid={`form-editor-field-${key.replace('.', '-')}-label`}
                        />
                      </div>
                      {locked ? (
                        <Badge variant="outline" className="gap-1 text-slate-500">
                          <Lock className="h-3 w-3" /> Pflichtfeld (fix)
                        </Badge>
                      ) : (
                        <>
                          <label className="flex items-center gap-1.5 text-xs text-slate-600">
                            <Switch
                              checked={!!spec.visible}
                              onCheckedChange={(v) => setFieldSpec(key, { visible: v })}
                              data-testid={`form-editor-field-${key.replace('.', '-')}-visible`}
                            />
                            Sichtbar
                          </label>
                          <label className="flex items-center gap-1.5 text-xs text-slate-600">
                            <Switch
                              checked={!!spec.required}
                              onCheckedChange={(v) => setFieldSpec(key, { required: v })}
                              disabled={!spec.visible}
                              data-testid={`form-editor-field-${key.replace('.', '-')}-required`}
                            />
                            Pflicht
                          </label>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Tipp: Wird „Kategorie" ausgeblendet, zeigt der Fach-Schritt alle Fächer in einer flachen Liste ohne Kategorie-Überschriften.
          Die Kategorien und Fächer selbst pflegen Sie oben im Bereich „Kategorien & Fächer".
        </p>
      </div>

      {/* Zusatzfelder */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-slate-700">Eigene Zusatzfelder</h4>
          <Button variant="outline" size="sm" onClick={addCustomField} data-testid="form-editor-add-custom-field-btn">
            <Plus className="h-4 w-4 mr-1" /> Feld hinzufügen
          </Button>
        </div>
        {(fc.custom_fields || []).length === 0 ? (
          <div className="text-sm text-slate-500 p-3 bg-slate-50 rounded-lg">
            Keine Zusatzfelder. Beispiele: „Wie sind Sie auf uns aufmerksam geworden?", „Leihinstrument gewünscht?".
          </div>
        ) : (
          <div className="space-y-3">
            {fc.custom_fields.map((cf, idx) => (
              <div key={cf.id} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`form-editor-custom-field-${idx}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <Input
                    value={cf.label}
                    onChange={(e) => updateCustomField(idx, { label: e.target.value })}
                    placeholder="Feldbezeichnung"
                    className="flex-1 min-w-[180px] h-8 text-sm"
                    data-testid={`form-editor-custom-field-${idx}-label`}
                  />
                  <Select value={cf.type} onValueChange={(v) => updateCustomField(idx, { type: v })}>
                    <SelectTrigger className="w-40 h-8 text-sm" data-testid={`form-editor-custom-field-${idx}-type`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CF_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={cf.step} onValueChange={(v) => updateCustomField(idx, { step: v })}>
                    <SelectTrigger className="w-48 h-8 text-sm" data-testid={`form-editor-custom-field-${idx}-step`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="student">Schritt: Schüler</SelectItem>
                      <SelectItem value="payer">Schritt: Zahler</SelectItem>
                    </SelectContent>
                  </Select>
                  <label className="flex items-center gap-1.5 text-xs text-slate-600">
                    <Switch
                      checked={!!cf.required}
                      onCheckedChange={(v) => updateCustomField(idx, { required: v })}
                      data-testid={`form-editor-custom-field-${idx}-required`}
                    />
                    Pflicht
                  </label>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-rose-500 hover:text-rose-700 h-8 w-8"
                    onClick={() => removeCustomField(idx)}
                    data-testid={`form-editor-custom-field-${idx}-delete`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                {cf.type === 'select' && (
                  <div>
                    <Label className="text-xs text-slate-500">Optionen (durch Komma getrennt)</Label>
                    <Input
                      value={(cf.options || []).join(', ')}
                      onChange={(e) => updateCustomField(idx, {
                        options: e.target.value.split(',').map((o) => o.trim()).filter(Boolean),
                      })}
                      placeholder="z. B. Ja, Nein, Vielleicht"
                      className="h-8 text-sm"
                      data-testid={`form-editor-custom-field-${idx}-options`}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end mt-5">
        <Button onClick={save} disabled={saving} data-testid="form-editor-save-btn">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Speichern…' : 'Formular speichern'}
        </Button>
      </div>
      </div>

      {/* Live-Vorschau (Split-Screen) */}
      <div className="xl:sticky xl:top-4">
        <FormLivePreview fc={fc} />
      </div>
      </div>
    </div>
  );
};
